import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloudOff, CloudUpload } from 'lucide-react';
import { useNetworkStatus } from '../services/networkStatus';
import { offlineQueue } from '../lib/offlineQueue';

/**
 * OfflineIndicator — Phase 1.8
 *
 * Slim status pill anchored to the bottom of the viewport (above the bottom
 * nav). Visible when EITHER:
 *   - the device is offline, or
 *   - there are pending items in the offline write queue (sync in progress).
 *
 * Stays out of the way when there's nothing to say.
 */
export default function OfflineIndicator() {
  const { t } = useTranslation();
  const { isOnline } = useNetworkStatus();
  const [pending, setPending] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const n = await offlineQueue.size();
        if (!cancelled) setPending(n);
      } catch {
        if (!cancelled) setPending(0);
      }
    };
    refresh();
    const unsub = offlineQueue.subscribe(refresh);
    // Re-check whenever connectivity flips
    const id = setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      unsub();
      clearInterval(id);
    };
  }, [isOnline]);

  if (isOnline && pending === 0) return null;

  const isSyncing = isOnline && pending > 0;
  const label = isSyncing
    ? t('offline.syncing', 'Syncing {{count}} pending…', { count: pending })
    : t('offline.youAreOffline', 'You are offline');
  const Icon = isSyncing ? CloudUpload : CloudOff;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
        insetInlineStart: 0,
        insetInlineEnd: 0,
        display: 'flex',
        justifyContent: 'center',
        zIndex: 1100,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '0.5rem 0.875rem',
          background: isSyncing ? 'var(--info-600)' : 'var(--warning-600)',
          color: 'var(--text-inverse)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          borderRadius: 'var(--radius-full)',
          boxShadow: 'var(--shadow-lg)',
          maxWidth: '90vw',
        }}
      >
        <Icon size={16} aria-hidden="true" />
        <span>{label}</span>
      </div>
    </div>
  );
}
